import { t } from "@/lib/i18n";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * One skeleton used to stand in for every route: four metric tiles and a tall
 * block, shown just as convincingly in front of the kanban board or a driver
 * profile. A placeholder that does not resemble what arrives is worse than
 * none — the layout visibly jumps when the data lands.
 */
export type LoadingVariant = "dashboard" | "table" | "board" | "detail";

export function PageLoading({ variant = "dashboard" }: { variant?: LoadingVariant }) {
  return <div className="flex flex-col gap-5" aria-busy="true" aria-label={t("spa_loading")}>
    <Skeleton className="h-10 w-64"/>
    {variant === "dashboard" ? <>
      <div className="grid gap-4 md:grid-cols-4">{Array.from({ length: 4 }, (_, index) => <Skeleton className="h-28" key={index}/>)}</div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]"><Skeleton className="h-96"/><Skeleton className="h-96"/></div>
    </> : null}
    {variant === "table" ? <><Skeleton className="h-11"/><TableSkeleton rows={8}/></> : null}
    {variant === "board" ? <div className="grid gap-3" style={{ gridAutoFlow: "column", gridAutoColumns: "minmax(230px, 1fr)" }}>
      {Array.from({ length: 5 }, (_, column) => <div className="flex flex-col gap-2" key={column}>
        <Skeleton className="h-10"/>
        {Array.from({ length: 3 - (column % 2) }, (_, card) => <Skeleton className="h-24" key={card}/>)}
      </div>)}
    </div> : null}
    {variant === "detail" ? <>
      <Skeleton className="h-36"/><Skeleton className="h-12"/>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]"><Skeleton className="h-80"/><Skeleton className="h-80"/></div>
    </> : null}
  </div>;
}

/** Rows for a list that is already framed by its own table chrome. */
export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return <div className="table-skeleton" aria-hidden="true">
    {Array.from({ length: rows }, (_, index) => <Skeleton className="h-12" key={index}/>)}
  </div>;
}
